<script setup lang="ts">
import { computed, ref } from 'vue';
import { searchPlaybook, type PlaybookInDb } from '@/api';
import GaiaAddButton from '@/components/design/base/GaiaAddButton.vue';
import GaiaPopover from '@/components/design/base/GaiaPopover.vue';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import GaiaSpinner from '@/components/design/base/GaiaSpinner.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaPickerOption from '@/components/design/composite/GaiaPickerOption.vue';
import { contractTypeNames } from '@/components/design/statusMaps';
import { extractErrorDetail } from '@/utils/errorHandler';
import { logger } from '@/utils/logger';

/**
 * The "+" beside the analysis tabs (Review Projects mock): pick a playbook and
 * an analysis for it is added to the project. Replaces the old flow of creating
 * an empty "Analysis N" and then attaching a playbook in its settings — the
 * playbook is what an analysis *is*, so it is chosen up front.
 */
const props = defineProps<{
  /** Playbook uids that already have an analysis — offered, but marked as added. */
  usedPlaybookUids: string[];
}>();

const emit = defineEmits<{ pick: [playbook: PlaybookInDb] }>();

const open = ref(false);
const query = ref('');
const loading = ref(false);
const playbooks = ref<PlaybookInDb[]>([]);

async function runSearch(text: string): Promise<void> {
  loading.value = true;
  try {
    const response = await searchPlaybook({
      body: { text, limit: 50, column_filters: { status: ['active'] } },
    });
    if (response.error) {
      throw new Error(extractErrorDetail(response.error, 'Failed to search playbooks'));
    }
    playbooks.value = response.data.results;
  } catch (error) {
    logger.error('Error searching playbooks:', error);
    playbooks.value = [];
  } finally {
    loading.value = false;
  }
}

function handleShow(show: boolean): void {
  open.value = show;
  if (show) {
    query.value = '';
    void runSearch('');
  }
}

const used = computed(() => new Set(props.usedPlaybookUids));

function meta(playbook: PlaybookInDb): string {
  const parts = [
    contractTypeNames(playbook.contract_types).join(', '),
    playbook.party_position,
  ].filter(Boolean);
  return parts.join(' · ');
}

function pick(playbook: PlaybookInDb): void {
  if (used.value.has(playbook.uid)) return;
  open.value = false;
  emit('pick', playbook);
}
</script>

<template>
  <gaia-popover
    trigger="click"
    :raw="false"
    :show="open"
    placement="bottom-start"
    @update:show="handleShow"
  >
    <template #trigger>
      <gaia-add-button subject="analysis from playbook" variant="icon" />
    </template>

    <n-flex vertical :size="8" :style="{ width: '296px' }">
      <gaia-text variant="label" size="nano" tone="faint">Add analysis from playbook</gaia-text>
      <gaia-search-input
        v-model:value="query"
        variant="panel"
        placeholder="Search playbooks…"
        :debounce="400"
        @search="runSearch"
      />
      <gaia-spinner v-if="loading && playbooks.length === 0" :style="{ margin: '18px auto' }" />
      <gaia-text v-else-if="playbooks.length === 0" size="dense" tone="faint">
        No playbooks match.
      </gaia-text>
      <n-scrollbar v-else style="max-height: 260px">
        <gaia-picker-option
          v-for="playbook in playbooks"
          :key="playbook.uid"
          :label="playbook.name"
          :meta="meta(playbook)"
          :disabled="used.has(playbook.uid)"
          @select="pick(playbook)"
        >
          <template v-if="used.has(playbook.uid)" #trailing>Added</template>
        </gaia-picker-option>
      </n-scrollbar>
    </n-flex>
  </gaia-popover>
</template>
